import { RestChatService } from '@repo/network'
import { ComponentNotificationVO, ComponentUserInfoVO, NotificationType } from '@repo/types'
import { UseMutateFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { createContext, useCallback, useContext } from 'react'

interface UserInfoContextProps {
  userInfo?: ComponentUserInfoVO
  changingUserAvailable: boolean
  requestChangeUserAvailable: UseMutateFunction<void, Error, boolean, unknown>
  updateUserInfoOnEvent: (vo: ComponentNotificationVO) => Promise<void>
}

const UserInfoContext = createContext<UserInfoContextProps>({
  userInfo: undefined,
  changingUserAvailable: false,
  requestChangeUserAvailable: () => undefined,
  updateUserInfoOnEvent: async () => undefined
})

interface UserInfoProps {
  children?: React.ReactNode
}

export const UserInfoProvider = ({ children }: UserInfoProps) => {
  const queryClient = useQueryClient()

  const { data: userInfo } = useQuery({
    queryKey: ['userInfo'],
    queryFn: async () => await RestChatService.userInfo()
  })

  const { mutate: requestChangeUserAvailable, isPending: changingUserAvailable } = useMutation({
    mutationKey: ['changeUserAvailable'],
    mutationFn: async (value: boolean) => {
      await RestChatService.changeUserAvailable(value)
      await queryClient.refetchQueries({ queryKey: ['userInfo'] })
    }
  })

  const updateUserInfoOnEvent = useCallback(
    async (vo: ComponentNotificationVO) => {
      if (vo.type === NotificationType.USER_AVAILABLE) {
        await queryClient.refetchQueries({ queryKey: ['userInfo'] })
      }
    },
    [queryClient]
  )

  return (
    <UserInfoContext.Provider
      value={{
        userInfo,
        changingUserAvailable,
        requestChangeUserAvailable,
        updateUserInfoOnEvent
      }}
    >
      {children}
    </UserInfoContext.Provider>
  )
}

export const useUserInfo = () => useContext(UserInfoContext)
